import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { convertMinutesToMilliseconds } from "@/utils/util.ts";
import { type GlobalStateModel } from "@/models.ts";
import { logout } from "@/services/permissions.ts";
import Popup from "@/components/shared/Popup";

const INACTIVITY_LIMIT_MINUTES = 30;
const WARNING_MINUTES = 3;
const INTERACTION_EVENTS = ["click", "mousemove", "scroll", "keypress"];

export default function InactivityHandler({
  globalState,
}: {
  globalState: GlobalStateModel;
}) {
  const instance = globalState.pca;
  const accounts = globalState.pca.getAllAccounts();
  const homeAccountId = accounts?.[0]?.homeAccountId;

  const inactivityLimit = useMemo(
    () => convertMinutesToMilliseconds(INACTIVITY_LIMIT_MINUTES),
    []
  );
  const warningStartTime = useMemo(
    () =>
      convertMinutesToMilliseconds(INACTIVITY_LIMIT_MINUTES - WARNING_MINUTES),
    []
  );

  // Refs for timers and for the latest "popup open" state, so handlers never go stale.
  const inactivityTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);
  const minuteIntervalRef = useRef<number | null>(null);
  const popupOpenRef = useRef<boolean>(false);

  // UI State
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupMinutesLeft, setPopupMinutesLeft] =
    useState<number>(WARNING_MINUTES);

  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (minuteIntervalRef.current) {
      clearInterval(minuteIntervalRef.current);
      minuteIntervalRef.current = null;
    }
  }, []);

  const logoutApp = useCallback(() => {
    if (!homeAccountId) return;
    clearAllTimers();
    logout(instance, homeAccountId);
  }, [instance, homeAccountId, clearAllTimers]);

  // Keep a ref mirror of popupOpen so event handlers always see the latest value.
  useEffect(() => {
    popupOpenRef.current = popupOpen;
  }, [popupOpen]);

  // Start the "warning countdown" (opens popup and ticks once per minute)
  const startWarning = useCallback(() => {
    setPopupOpen(true);
    setPopupMinutesLeft(WARNING_MINUTES);

    // Use functional updates to avoid stale closures.
    minuteIntervalRef.current = window.setInterval(() => {
      setPopupMinutesLeft((prev) => {
        const next = Math.max(prev - 1, 0);
        return next;
      });
    }, 60_000);
  }, []);

  // Reset the inactivity system (called on user activity or "Stay Logged In")
  const resetInactivity = useCallback(() => {
    // Update last active time
    localStorage.setItem("timeLastActive", new Date().toISOString());

    // Reset UI + timers
    setPopupOpen(false);
    setPopupMinutesLeft(WARNING_MINUTES);
    clearAllTimers();

    // Set up fresh timers from now
    warningTimerRef.current = window.setTimeout(startWarning, warningStartTime);
    inactivityTimerRef.current = window.setTimeout(logoutApp, inactivityLimit);
  }, [
    clearAllTimers,
    startWarning,
    warningStartTime,
    inactivityLimit,
    logoutApp,
  ]);

  // Single handler for any user interaction
  const handleUserInteraction = useCallback(() => {
    // If popup is showing, let the user choose - don't auto-extend.
    if (popupOpenRef.current) return;
    resetInactivity();
  }, [resetInactivity]);

  // On mount: restore from previous session + attach listeners; on unmount: cleanup.
  useEffect(() => {
    // 1) Handle active sessions from before the app loaded
    const timeLastActive = localStorage.getItem("timeLastActive");
    const now = Date.now();

    if (timeLastActive) {
      const elapsed = now - new Date(timeLastActive).getTime();
      if (elapsed >= inactivityLimit) {
        logoutApp();
        return;
      }

      // Compute remaining durations
      const remainingToLogout = Math.max(inactivityLimit - elapsed, 0);
      const remainingToWarning = Math.max(warningStartTime - elapsed, 0);

      // If we already crossed the warning threshold, open the popup immediately
      if (elapsed >= warningStartTime) {
        startWarning();

        // Align the logout to the remaining time
        inactivityTimerRef.current = window.setTimeout(
          logoutApp,
          remainingToLogout
        );
      } else {
        // Not yet at warning time: schedule both warning + logout
        warningTimerRef.current = window.setTimeout(
          startWarning,
          remainingToWarning
        );
        inactivityTimerRef.current = window.setTimeout(
          logoutApp,
          remainingToLogout
        );
      }
    } else {
      // First load: set a last active time and start timers
      localStorage.setItem("timeLastActive", new Date().toISOString());
      warningTimerRef.current = window.setTimeout(
        startWarning,
        warningStartTime
      );
      inactivityTimerRef.current = window.setTimeout(
        logoutApp,
        inactivityLimit
      );
    }

    // 2) Attach listeners once
    INTERACTION_EVENTS.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, {
        passive: true,
      });
    });

    // Also consider when tab becomes visible again as activity
    const onVisibility = () => {
      if (!document.hidden) handleUserInteraction();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Cleanup on unmount
    return () => {
      INTERACTION_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
      });
      document.removeEventListener("visibilitychange", onVisibility);
      clearAllTimers();
    };
  }, [
    inactivityLimit,
    warningStartTime,
    handleUserInteraction,
    clearAllTimers,
    startWarning,
    logoutApp,
  ]);

  return (
    <>
      <Popup
        show={popupOpen}
        header="Session Inactivity"
        buttonText="Stay Logged In"
        secondButtonText="Logout Now"
        onButtonClick={resetInactivity}
        onClose={resetInactivity}
        onSecondButtonClick={logoutApp}
      >
        Your session will end in {popupMinutesLeft} minute
        {popupMinutesLeft === 1 ? "" : "s"} due to inactivity. Do you want to
        extend your session?
      </Popup>
    </>
  );
}
